import type { Expr } from '../core/types';
import { GLSL_SAFE, SAFE } from './glsl';

export type BandResolver = (name: string) => Expr | null;
export type ParseResult = { ok: true; node: Expr } | { ok: false; error: string };

/**
 * Recursive descent over:
 *   expr   := term (('+' | '-') term)*
 *   term   := factor (('*' | '/') factor)*
 *   factor := '-' factor | number | ident | '(' expr ')'
 *
 * `resolve` turns a band name into an expression, so the parser is testable with no
 * raster loaded. Division is guarded exactly as the built-in indices are, and numeric
 * literals are rejected at parse time if they have no safe GLSL form — a user-typed
 * `1e-9` would otherwise kill the shader the same way a hand-written one did.
 */
export function parseIndexExpr(src: string, resolve: BandResolver): ParseResult {
  const text = String(src ?? '').trim();   // trailing space leaves nothing to match
  const toks: string[] = [];
  const re = /\s*([A-Za-z_][A-Za-z_0-9]*|\d*\.?\d+(?:[eE][-+]?\d+)?|[()+\-*/])/y;
  let pos = 0;
  while (pos < text.length) {
    re.lastIndex = pos;
    const m = re.exec(text);
    if (!m) return { ok: false, error: `Unexpected character at position ${pos + 1}: "${text[pos]}"` };
    toks.push(m[1]!);
    pos = re.lastIndex;
  }
  if (!toks.length) return { ok: false, error: 'Empty expression' };

  let i = 0;
  let err: string | null = null;
  const peek = () => toks[i];
  const eat = (t: string) => (toks[i] === t ? (i++, true) : false);

  function factor(): Expr | null {
    if (err) return null;
    if (eat('-')) { const f = factor(); return f === null ? null : ['-', 0, f]; }
    if (eat('(')) {
      const e = expr();
      if (!eat(')') && !err) err = 'Unclosed bracket';
      return e;
    }
    const t = peek();
    if (t === undefined) { err = 'Expression ends unexpectedly'; return null; }
    if (/^[A-Za-z_]/.test(t)) {
      i++;
      const r = resolve(t);
      if (r == null) { err = `Unknown band "${t}"`; return null; }
      return r;
    }
    if (/^[\d.]/.test(t)) {
      i++;
      const n = parseFloat(t);
      if (!Number.isFinite(n)) { err = `Bad number "${t}"`; return null; }
      if (!GLSL_SAFE(n)) { err = `Number "${t}" has no safe GLSL form — use plain decimal`; return null; }
      return n;
    }
    err = `Unexpected "${t}"`;
    return null;
  }
  function term(): Expr | null {
    let l = factor();
    while (!err && (peek() === '*' || peek() === '/')) {
      const op = toks[i++]!;
      const r = factor();
      if (err) return null;
      l = op === '*' ? ['*', l!, r!] : ['/', l!, SAFE(r!)];
    }
    return l;
  }
  function expr(): Expr | null {
    let l = term();
    while (!err && (peek() === '+' || peek() === '-')) {
      const op = toks[i++]!;
      const r = term();
      if (err) return null;
      l = [op, l!, r!];
    }
    return l;
  }

  const node = expr();
  if (err) return { ok: false, error: err };
  if (i < toks.length) return { ok: false, error: `Unexpected "${toks[i]}" after a complete expression` };
  return { ok: true, node: node! };
}
